import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  AspectRatio,
  Box,
  Input,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Radio,
  RadioGroup,
  SimpleGrid,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  useStyleConfig,
} from "@chakra-ui/react";
import { ASSETS_SLIDER_PANEL, IMAGES_SLIDER_PANEL } from "../../details/constants";
import { getAssetPostEditorPayload, getImagePostEditorPayload } from "../../../TldrCalendarPlanner/utils";
import { switchMediaTab } from "../../../_actions/sidebarSliderActions";
import ImagesList from "../../../TldrCalendarPlanner/Components/TldrMediaSidebar/ImagesList";
import AssetsList from "../../../TldrCalendarPlanner/Components/TldrMediaSidebar/AssetsList";
import { BACKGROUND_TYPES, BACKGROUND_COLOR_PRESETS, BACKGROUND_GRADIENT_PRESETS } from "./snapshotMaker.constants";

const MEDIA_TYPE_TABS = [
  ASSETS_SLIDER_PANEL,
  IMAGES_SLIDER_PANEL,
];

const parseGradient = (string) => {
  const data = {
    type: "linear",
    angle: 180,
    stops: [],
  };
  const matches = /^([a-z-]+)-gradient\((.*)\)$/.exec(string);

  if (["radial", "linear"].includes(matches?.[1])) {
    data.type = matches[1];

    const items = (matches[2] || "").split(",").map((i) => i.trim());

    if (data.type === "linear") {
      const angleMatches = /^([0-9]+)deg$/.exec(items[0]);

      if (angleMatches) {
        data.angle = angleMatches?.[1] || 0;

        items.shift();
      }
    }

    items.forEach((item) => {
      const stopMatches = /^(.*?)(| ([0-9]+)%)$/.exec(item);

      if (stopMatches) {
        data.stops.push({
          color: stopMatches[1],
          offset: stopMatches[3] || undefined,
        });
      }
    });
  }

  if (data.stops.length < 2) {
    if (!data.stops.length) {
      data.stops.push({
        color: "#ffffff",
      });
    }

    data.stops.push({
      color: "#000000",
    });
  }

  return data;
};

const stringifyGradient = (object) => {
  const items = [];

  if (object.type === "linear" && object.angle !== undefined) {
    items.push(`${object.angle}deg`);
  }

  object.stops?.forEach((item) => {
    items.push(`${item.color}${item.offset !== undefined ? ` ${item.offset}%` : ""}`);
  });

  return `${object.type}-gradient(${items.join(", ")})`;
};

const SnapshotMakerBackgroundSettings = ({
  config = {},
  onConfigChange,
}) => {
  const { t } = useTranslation(["snapshot-maker"]);
  const radioGroupStyles = useStyleConfig('RadioGroup', { variant: "switch" });
  const [selectedTab, setSelectedTab] = useState();
  const [gradientEditor, setGradientEditor] = useState(null);
  const dispatch = useDispatch();
  const sidebarSlider = useSelector((state) => state.sidebarSlider);

  const getMediaTypeFromIndex = useCallback((index) => {
    return MEDIA_TYPE_TABS[index] || MEDIA_TYPE_TABS[0];
  }, []);

  const handleConfigChange = useCallback((data) => {
    onConfigChange?.(data);
  }, [onConfigChange]);

  const handleTabChange = useCallback((tab) => {
    setSelectedTab(tab);
  }, [setSelectedTab]);

  const handleGradientEditorOpen = useCallback(() => {
    setGradientEditor(parseGradient(config.type === BACKGROUND_TYPES.gradient ? config.value : ""));
  }, [setGradientEditor, config]);

  const handleGradientEditorClose = useCallback(() => {
    setGradientEditor(null);
  }, [setGradientEditor]);

  useEffect(() => {
    if (gradientEditor) {
      const gradientValue = stringifyGradient(gradientEditor);

      if (config.type !== BACKGROUND_TYPES.gradient || config.value !== gradientValue) {
        handleConfigChange({
          type: BACKGROUND_TYPES.gradient,
          value: stringifyGradient(gradientEditor),
        });
      }
    }
  }, [gradientEditor]);

  useEffect(() => {
    setSelectedTab(config.type);
  }, [config.type]);

  useEffect(() => {
    dispatch(switchMediaTab(getMediaTypeFromIndex(0)));
  }, []);

  return (
    <Box>
      <Box marginBottom="4">
        <RadioGroup
          onChange={handleTabChange}
          value={selectedTab}
          variant="switch"
          __css={radioGroupStyles}
        >
          {Object.values(BACKGROUND_TYPES).map((type) => (
            <Radio key={type} value={type}>
              {t(`background.${type}`)}
            </Radio>
          ))}
        </RadioGroup>
      </Box>
      {selectedTab === BACKGROUND_TYPES.color && (
        <Box>
          <Box>
            <Input
              type="color"
              value={config.type === BACKGROUND_TYPES.color ? config.value : "#ffffff"}
              onChange={(event) => {
                handleConfigChange({
                  type: BACKGROUND_TYPES.color,
                  value: event.target.value,
                });
              }}
              onClick={(event) => {
                if (config.type !== BACKGROUND_TYPES.color) {
                  handleConfigChange({
                    type: BACKGROUND_TYPES.color,
                    value: event.target.value,
                  });
                }
              }}
            />
          </Box>
          <Box maxHeight="320px" overflowY="auto" padding="2" margin="-2" marginTop="2">
            <SimpleGrid spacing="2" gridTemplateColumns="repeat(auto-fit, minmax(35px, max-content))">
              {BACKGROUND_COLOR_PRESETS.map((color) => (
                <AspectRatio
                  key={color}
                  ratio={1}
                  width="40px"
                  maxWidth="100%"
                  borderRadius="sm"
                  border="1px solid"
                  borderColor="whiteAlpha.100"
                  outline={config.type === BACKGROUND_TYPES.color && color === config.value ? "2px solid" : null}
                  outlineOffset="1px"
                  outlineColor="primary.400"
                  cursor="pointer"
                  onClick={() => {
                    handleConfigChange({
                      type: BACKGROUND_TYPES.color,
                      value: color,
                    });
                  }}
                >
                  <Box background={color} />
                </AspectRatio>
              ))}
            </SimpleGrid>
          </Box>
        </Box>
      )}
      {selectedTab === BACKGROUND_TYPES.gradient && (
        <Box>
          <Box>
            <Popover
              isLazy
              isOpen={gradientEditor}
              onOpen={handleGradientEditorOpen}
              onClose={handleGradientEditorClose}
            >
              <PopoverTrigger>
                <Box
                  width="full"
                  height="10"
                  borderRadius="8px"
                  border="1px solid"
                  borderColor="whiteAlpha.100"
                  background="brand.dark"
                  paddingX="4"
                  paddingY="1"
                  cursor="pointer"
                  _hover={{
                    background: "brand.darker",
                    borderColor: "gray.1000"
                  }}
                >
                  <Box
                    width="full"
                    height="full"
                    border="1px solid"
                    borderColor="gray.400"
                    bgGradient={config.type === BACKGROUND_TYPES.gradient ? config.value : ""}
                  />
                </Box>
              </PopoverTrigger>
              <PopoverContent width="auto">
                <PopoverArrow />
                <PopoverBody>
                  <Box textAlign="center">
                    <Box fontSize="xs" color="gray.100" textTransform="uppercase" marginBottom="2">
                      {t("gradient.type_title")}
                    </Box>
                    <RadioGroup
                      onChange={(value) => setGradientEditor((data) => ({ ...data, type: value })) }
                      value={gradientEditor?.type}
                      variant="switch"
                      size="sm"
                      __css={radioGroupStyles}
                    >
                      <Radio value="linear">
                        {t(`gradient.type.linear`)}
                      </Radio>
                      <Radio value="radial">
                        {t(`gradient.type.radial`)}
                      </Radio>
                    </RadioGroup>
                  </Box>
                </PopoverBody>
              </PopoverContent>
            </Popover>
          </Box>
          <Box maxHeight="320px" overflowY="auto" padding="2" margin="-2" marginTop="2">
            <SimpleGrid spacing="2" gridTemplateColumns="repeat(auto-fit, minmax(60px, max-content))">
              {BACKGROUND_GRADIENT_PRESETS.map((gradient) => (
                <AspectRatio
                  key={gradient}
                  ratio={1}
                  width="70px"
                  maxWidth="100%"
                  borderRadius="sm"
                  border="1px solid"
                  borderColor="whiteAlpha.100"
                  outline={config.type === BACKGROUND_TYPES.gradient && gradient === config.value ? "2px solid" : null}
                  outlineOffset="1px"
                  outlineColor="primary.400"
                  cursor="pointer"
                  onClick={() => {
                    handleConfigChange({
                      type: BACKGROUND_TYPES.gradient,
                      value: gradient,
                    });
                  }}
                >
                  <Box bgGradient={gradient} />
                </AspectRatio>
              ))}
            </SimpleGrid>
          </Box>
        </Box>
      )}
      {selectedTab === BACKGROUND_TYPES.image && (
        <Box>
          <Tabs
            isLazy
            defaultIndex={0}
            colorScheme="primary"
            index={MEDIA_TYPE_TABS.indexOf(sidebarSlider.sliderPanelType)}
            onChange={(index) => {
              dispatch(switchMediaTab(getMediaTypeFromIndex(index)));
            }}
          >
            <TabList>
              <Tab>{t("media_tab.assets")}</Tab>
              <Tab>{t("media_tab.photos")}</Tab>
            </TabList>
            <TabPanels minHeight="200px">
              <TabPanel>
                <AssetsList
                  onItemClick={(item) => {
                    const postEditorPayload = getAssetPostEditorPayload(item);

                    handleConfigChange({
                      type: BACKGROUND_TYPES.image,
                      value: postEditorPayload.url,
                    });
                  }}
                  disallowVideos
                />
              </TabPanel>
              <TabPanel>
                <ImagesList
                  onItemClick={(item) => {
                    const postEditorPayload = getImagePostEditorPayload(item);
                    const baseMimeType = postEditorPayload.mimeType.split("/")[0];

                    if (baseMimeType === "image") {
                      handleConfigChange({
                        type: BACKGROUND_TYPES.image,
                        value: postEditorPayload.url,
                      });
                    }
                  }}
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>
      )}
    </Box>
  );
};

SnapshotMakerBackgroundSettings.propTypes = {
  config: PropTypes.shape({
    type: PropTypes.oneOf(Object.values(BACKGROUND_TYPES)),
    value: PropTypes.string,
  }),
  onConfigChange: PropTypes.func,
};

SnapshotMakerBackgroundSettings.defaultProps = {
  config: undefined,
  onConfigChange: undefined,
};

export default SnapshotMakerBackgroundSettings;
